/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from "react"
import { dataService } from "../../../services/dataService"
import { useNavigate, useParams } from "react-router-dom";
import config from "../../../config";
import { Auth } from "aws-amplify";
import { io } from "socket.io-client";
import LotCard from "./LotCard";
import PlaceBidCard from "./PlaceBidCard";
import { Triangle } from "react-loader-spinner";

export default function CarouselView(){

    const {id, uid} = useParams();
    const [carouselData, setCarouselData] = useState({})
    const [socket,setSocket]=useState(null);
    const [lots, setLots] = useState([])
    const [currentLotIndex, setCurrentLotIndex] = useState(0);
    const [placeBidQueue, setPlaceBidQueue] = useState([]);
    const [currentPlaceBid, setCurrentPlaceBid] = useState(null);
    const [showPlaceBid, setShowPlaceBid] = useState(false);
    const [pausedLotIndex, setPausedLotIndex] = useState(null);
    const [auctionData, setAutcionData] = useState({});
    const [loading, setLoading] = useState(true);
    const Navigate = useNavigate();

    const getLeaderboardData = async ()=>{
        const response = await dataService.GetLeaderboard(uid)
        if(response.error==false){
            setCarouselData(response.data.data.event_display)
            setAutcionData(response.data.data);
            if(response.data.data.status!=="Accepting bids" && response.data.data.status!=="Published"){
                Navigate('/page-not-found');
            }
        }
    }

    const preloadImages = (lots) => {
        const imagePromises = lots.map(lot => {
            return new Promise((resolve, reject) => {
                const featuredImage = lot.images.find(image => image.featured);
                if (featuredImage) {
                    const img = new Image();
                    img.onload = () => resolve();
                    img.onerror = () => resolve(); // Resolve anyway to continue
                    img.src = `${config.Storage.CDN}${featuredImage.url}`;
                } else {
                    resolve();
                }
            });
        });

        Promise.allSettled(imagePromises).then(() => {
            setLoading(false);
        });
    };

    const wakeLockRef = useRef(null);

    useEffect(() => {
        let isSupported = "wakeLock" in navigator;

        const requestWakeLock = async () => {
            try {
                if (isSupported) {
                    wakeLockRef.current = await navigator.wakeLock.request("screen");
                    console.log("Wake lock is active");
                    wakeLockRef.current.addEventListener("release", () => {
                        console.log("Wake lock was released");
                    });
                }
            } catch (err) {
                console.error(`${err.name}, ${err.message}`);
            }
        };

        // Initial request
        requestWakeLock();

        // Re-request on visibility change
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                requestWakeLock();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            if (wakeLockRef.current) {
                wakeLockRef.current.release();
                wakeLockRef.current = null;
            }
        };
    }, []);


    const socketConnection=async(lots)=>{
        const idToken = await Auth.currentSession()
    
        if(socket==null){
            const socketConn= io(config.SOCKETURL, {
                transports: ['websocket'],
                auth: {
                    token: idToken.getIdToken().getJwtToken(),
                    type: "seller"
                },
                reconnection: true
            })
    
            setSocket(socketConn);
    
            lots.map((item)=>{
                socketConn.on('authorization', (message)=>{
                    if(message.success_status==true){
                        socketConn.emit('joinBidRoom', item?._id, "seller");
                    }
                })
            })
                
            socketConn?.on('placeBid',(message)=>{
                if(message.success==true && message.newCurrentLotDetails){
                    const lotDetails = message.newCurrentLotDetails;
                    setLots(prevLots => 
                        prevLots.map(lot => 
                            lot._id === lotDetails._id 
                                ? {
                                    ...lot,
                                    ...(lotDetails.bid_amount && { current_bid: lotDetails.bid_amount }),
                                    ...(lotDetails.paddle_number && { paddle_number: lotDetails.paddle_number })
                                }
                                : lot
                        )
                    );
                    setPlaceBidQueue(prev => [...prev, lotDetails._id]);
                    setCurrentLotIndex(prevIndex => {
                        if (!showPlaceBid) {
                            setPausedLotIndex(prevIndex);
                        }
                        return prevIndex;
                    });
                }
            })

            socketConn?.on('sellerListBidHistory', (response) => {
                if(response?.success === true && response?.newResponse){
                    const lotIdFromBids = response.newResponse.all_bidders?.[0]?.lot_id;
                    if(lotIdFromBids){
                        const newTopBid = response.newResponse.all_bidders[0];
                        setLots(prevLots => 
                            prevLots.map(lot => 
                                lot._id === lotIdFromBids 
                                    ? {
                                        ...lot,
                                        current_bid: newTopBid?.bid_amount || 0,
                                        paddle_number: newTopBid?.paddle_number || ''
                                    }
                                    : lot
                            )
                        );
                    }
                }
            })
    
        }else{
            lots.map((item)=>{
                socket.emit('joinBidRoom', item?._id, "seller");
            })
        }
    }

    const fetchLots = async() =>{
        let endpoint = `?auction_id=${uid}&per_page=500`
        const response = await dataService.LotList(endpoint)
        if(response.error==false){
            setLots(response.data.data)
            socketConnection(response.data.data)
            preloadImages(response.data.data);
        }
    }

    useEffect(() => {
        if (!showPlaceBid) {
            const interval = setInterval(() => {
                setCurrentLotIndex((prevIndex) => {
                    const nextIndex = prevIndex + 1;
                    return nextIndex >= lots.length ? 0 : nextIndex;
                });
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [lots.length, showPlaceBid]);

    useEffect(() => {
        if (!showPlaceBid && placeBidQueue.length > 0) {
            const nextBid = lots.find(lot => lot._id === placeBidQueue[0]) || currentLot;
            setCurrentPlaceBid(nextBid);
            setShowPlaceBid(true);
            setPlaceBidQueue(prev => prev.slice(1));
            return;
        }

        if (showPlaceBid) {
            const timer = setTimeout(() => {
                if (placeBidQueue.length > 0) {
                    const nextBid = lots.find(lot => lot._id === placeBidQueue[0]) || null;
                    setCurrentPlaceBid(nextBid);
                    setPlaceBidQueue(prev => prev.slice(1));
                } else {
                    setShowPlaceBid(false);
                    setCurrentPlaceBid(null);
                    setCurrentLotIndex(pausedLotIndex);
                    setPausedLotIndex(null);
                }
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [showPlaceBid, placeBidQueue, lots, pausedLotIndex]);



    const currentLot = lots[currentLotIndex];

    useEffect(()=>{
        getLeaderboardData()
        fetchLots()
    },[])

    return(
        <div className="min-h-screen flex items-center justify-center" >
            {!loading && Object.keys(carouselData).length>0 && 
            <div
                className="relative bg-cover bg-[#E6E6E6]"
                style={{ aspectRatio: '16/9', 
                    width: '100vw',
                    height: '56.25vw',
                    maxWidth: '177.78vh',
                    maxHeight: '100vh',
                    backgroundColor : carouselData?.background_color, 
                    backgroundImage: `url(${config.Storage.CDN}${carouselData?.background_image})`}}
            >
                <div className="absolute top-0 left-0 right-0 px-[4%] pt-[4%] h-[17%] flex justify-between ">
                    {carouselData?.left_logo_image && carouselData?.left_logo_image!=="" && 
                    <img src={`${config.Storage.CDN}${carouselData?.left_logo_image}`} 
                        className="h-full w-auto" alt="Left logo image" />}
                    {carouselData?.right_logo_image && carouselData?.right_logo_image!=="" && 
                    <img src={`${config.Storage.CDN}${carouselData?.right_logo_image}`} 
                        className="h-full w-auto" alt="Right logo image" />}
                </div>
                <div className="absolute inset-0 flex flex-col">
                    <div className="h-[10%] " >
                    </div>
                    <div className="h-full max-h-[90%] flex items-center justify-center" >
                        {currentLot && !showPlaceBid && <LotCard currentLot={currentLot} currency={auctionData.currency} />}
                        {showPlaceBid && currentPlaceBid && <PlaceBidCard currentLot={currentPlaceBid} currency={auctionData.currency} />}
                    </div>
                </div>
                <div className="text-[#FFFFFF] flex absolute justify-end gap-2 items-end bottom-[8%] right-[6%]" style={{ fontSize: "0.73vw" }} >
                    Powered by<span><img src='/images/indy-logo.svg' 
                        style={{ height: "1.35vw", width: "auto" }}
                    /></span>
                </div>
            </div>}
            {loading && (
                <div className="fixed inset-0 bg-[#E6E6E6] flex justify-center items-center z-[9999]">
                    <Triangle
                        height="80"
                        width="80"
                        color="#000"
                        ariaLabel="triangle-loading"
                        visible={true}
                    />
                </div>
            )}
        </div>
    )
}